(function () {
  const roots = document.querySelectorAll('[data-predictive-search]');
  if (!roots.length) return;

  roots.forEach((root) => {
    const input = root.querySelector('[data-predictive-search-input]');
    const panel = root.querySelector('[data-predictive-search-panel]');
    const resultsEl = root.querySelector('[data-predictive-search-results]');
    const viewAllLink = root.querySelector('[data-predictive-search-view-all]');

    if (!input || !panel || !resultsEl || !viewAllLink) return;

    // Sectie containers per root
    const containers = {
      products: root.querySelector('[data-predictive-search-products]'),
      collections: root.querySelector('[data-predictive-search-categories]'),
      pages: root.querySelector('[data-predictive-search-pages]'),
      articles: root.querySelector('[data-predictive-search-blogs]'),
    };

    // Sectie wrappers (voor hide/show)
    const sectionWrappers = {
      products: resultsEl.querySelector('[data-section="products"]'),
      collections: resultsEl.querySelector('[data-section="collections"]'),
      pages: resultsEl.querySelector('[data-section="pages"]'),
      articles: resultsEl.querySelector('[data-section="articles"]'),
    };

    // ---- Instellingen per root (optioneel override via data-attributes) ----
    const caps = {
      products: Number(root.dataset.capProducts || 8),
      collections: Number(root.dataset.capCollections || 4),
      pages: Number(root.dataset.capPages || 4),
      articles: Number(root.dataset.capArticles || 4),
    };

    const TYPE_PARAM = { products: 'product', collections: 'collection', pages: 'page', articles: 'article' };
    // Labels (show more en subtitles) via data-attributes, met fallback
    const LABELS = {
      products: {
        more: root.dataset.labelMoreProducts || 'Toon meer producten',
        subtitle: root.dataset.labelSubtitleProducts || 'Product',
      },
      collections: {
        more: root.dataset.labelMoreCollections || 'Toon meer categorieën',
        subtitle: root.dataset.labelSubtitleCollections || 'Categorie',
      },
      pages: {
        more: root.dataset.labelMorePages || 'Toon meer pagina’s',
        subtitle: root.dataset.labelSubtitlePages || 'Pagina',
      },
      articles: {
        more: root.dataset.labelMoreArticles || 'Toon meer blogs',
        subtitle: root.dataset.labelSubtitleArticles || 'Blog',
      },
    };

    const MAX_CAP = Math.max(...Object.values(caps));
    const FETCH_LIMIT = MAX_CAP + 1; // haal net iets meer op om "meer" te detecteren

    let controller = null;
    let activeIndex = -1;
    let items = [];

    const openPanel = () => {
      panel.hidden = false;
      panel.classList.remove('hidden');
      input.setAttribute('aria-expanded', 'true');
    };

    const closePanel = () => {
      panel.hidden = true;
      panel.classList.add('hidden');
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    };

    const debounce = (fn, wait) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    };

    // --- Renderers ---
    function makeProductItem({ url, title, image, priceText, badge }) {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '-1');
      li.className = 'aria-selected:bg-gray-100 rounded-md';

      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('tabindex', '-1');
      a.className = 'flex items-center gap-3 p-2 no-underline';

      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.className = 'h-14 w-14 rounded-md object-cover bg-gray-50';
        a.appendChild(img);
      } else {
        const ph = document.createElement('div');
        ph.className = 'h-14 w-14 rounded-md bg-gray-50';
        a.appendChild(ph);
      }

      const meta = document.createElement('div');
      meta.className = 'min-w-0 flex-1';

      const titleEl = document.createElement('div');
      titleEl.className = 'truncate text-sm leading-tight';
      titleEl.textContent = title;
      meta.appendChild(titleEl);

      const subRow = document.createElement('div');
      subRow.className = 'mt-0.5 flex items-center gap-2';

      if (priceText) {
        const priceEl = document.createElement('span');
        priceEl.className = 'text-xs text-gray-700';
        priceEl.textContent = priceText;
        subRow.appendChild(priceEl);
      }

      if (badge) {
        const badgeEl = document.createElement('span');
        badgeEl.className = 'rounded-full border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600';
        badgeEl.textContent = badge;
        subRow.appendChild(badgeEl);
      }

      meta.appendChild(subRow);
      a.appendChild(meta);
      li.appendChild(a);
      return li;
    }

    function makeGenericItem({ url, title, subtitle, icon }) {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '-1');
      li.className = 'aria-selected:bg-gray-100 rounded-md';

      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('tabindex', '-1');
      a.className = 'flex items-center gap-3 px-2 py-2 no-underline';

      const box = document.createElement('div');
      box.className = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-50 text-xs text-gray-500';
      box.textContent = icon || '↳';
      a.appendChild(box);

      const meta = document.createElement('div');
      meta.className = 'min-w-0';

      const t = document.createElement('div');
      t.className = 'truncate text-sm leading-tight';
      t.textContent = title;
      meta.appendChild(t);

      if (subtitle) {
        const s = document.createElement('div');
        s.className = 'text-xs text-gray-600';
        s.textContent = subtitle;
        meta.appendChild(s);
      }

      a.appendChild(meta);
      li.appendChild(a);
      return li;
    }

    function makeShowMoreItem(text, href) {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '-1');
      li.className = 'aria-selected:bg-gray-100 rounded-md';

      const a = document.createElement('a');
      a.href = href;
      a.setAttribute('tabindex', '-1');
      a.className = 'flex items-center gap-3 px-2 py-2 no-underline';

      const box = document.createElement('div');
      box.className = 'flex h-9 w-9 items-center justify-center rounded-md bg-gray-50 text-base leading-none';
      box.textContent = '…';
      a.appendChild(box);

      const t = document.createElement('div');
      t.className = 'text-sm';
      t.textContent = text;

      a.appendChild(t);
      li.appendChild(a);
      return li;
    }

    function setActive(index) {
      items.forEach((li, i) => li.setAttribute('aria-selected', i === index ? 'true' : 'false'));
      activeIndex = index;
    }

    function formatMoney(cents) {
      try {
        return Shopify.formatMoney(cents);
      } catch (e) {
        return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || '{{ shop.currency }}' });
      }
    }

    async function fetchSuggestions(q) {
      if (controller) controller.abort();
      controller = new AbortController();

      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': String(FETCH_LIMIT), // bijv. 7 als je cap=6 hanteert
        'resources[limit_scope]': 'each', // <<< belangrijk: per type i.p.v. totaal
        // Optioneel:
        'resources[options][unavailable_products]': 'last',
        // 'resources[options][fields]': 'title,product_type,variants.title,vendor'
      });

      const res = await fetch('/search/suggest.json?' + params.toString(), {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!res.ok) throw new Error('Predictive search error');
      return res.json();
    }

    function clearSections() {
      Object.values(containers).forEach((ul) => {
        if (ul) ul.innerHTML = '';
      });
      Object.values(sectionWrappers).forEach((li) => {
        if (li) li.classList.add('hidden');
      });
    }

    function buildResults(json, q) {
      clearSections();
      items = [];

      const results = json?.resources?.results || {};
      let totalCount = 0;

      // ---- Products ----
      const prods = results.products || [];
      if (prods.length && containers.products && sectionWrappers.products) {
        const cap = caps.products;
        sectionWrappers.products.classList.remove('hidden');

        prods.slice(0, cap).forEach((entry) => {
          const url = entry?.url;
          const title = entry?.title;
          const image = entry?.image?.src || entry?.featured_image?.url || null;
          const priceCents = entry?.price ?? entry?.price_min;
          const priceText = typeof priceCents === 'number' ? formatMoney(priceCents) : null;

          const li = makeProductItem({
            url,
            title,
            image,
            priceText,
            badge: entry?.vendor || null,
          });
          containers.products.appendChild(li);
          items.push(li);
          totalCount++;
        });

        if (prods.length > cap) {
          const href = `/search?q=${encodeURIComponent(q)}&type=${TYPE_PARAM.products}`;
          const more = makeShowMoreItem(LABELS.products.more, href);
          containers.products.appendChild(more);
          items.push(more);
          totalCount++;
        }
      }

      // ---- Collections ----
      const cols = results.collections || [];
      if (cols.length && containers.collections && sectionWrappers.collections) {
        const cap = caps.collections;
        sectionWrappers.collections.classList.remove('hidden');

        cols.slice(0, cap).forEach((entry) => {
          const url = entry?.url;
          const title = entry?.title;
          const li = makeGenericItem({ url, title, subtitle: LABELS.collections.subtitle, icon: '#' });
          containers.collections.appendChild(li);
          items.push(li);
          totalCount++;
        });

        if (cols.length > cap) {
          const href = `/search?q=${encodeURIComponent(q)}&type=${TYPE_PARAM.collections}`;
          const more = makeShowMoreItem(LABELS.collections.more, href);
          containers.collections.appendChild(more);
          items.push(more);
          totalCount++;
        }
      }

      // ---- Pages ----
      const pgs = results.pages || [];
      if (pgs.length && containers.pages && sectionWrappers.pages) {
        const cap = caps.pages;
        sectionWrappers.pages.classList.remove('hidden');

        pgs.slice(0, cap).forEach((entry) => {
          const url = entry?.url;
          const title = entry?.title;
          const li = makeGenericItem({ url, title, subtitle: LABELS.pages.subtitle, icon: 'Pg' });
          containers.pages.appendChild(li);
          items.push(li);
          totalCount++;
        });

        if (pgs.length > cap) {
          const href = `/search?q=${encodeURIComponent(q)}&type=${TYPE_PARAM.pages}`;
          const more = makeShowMoreItem(LABELS.pages.more, href);
          containers.pages.appendChild(more);
          items.push(more);
          totalCount++;
        }
      }

      // ---- Articles ----
      const arts = results.articles || [];
      if (arts.length && containers.articles && sectionWrappers.articles) {
        const cap = caps.articles;
        sectionWrappers.articles.classList.remove('hidden');

        arts.slice(0, cap).forEach((entry) => {
          const url = entry?.url;
          const title = entry?.title;
          const li = makeGenericItem({ url, title, subtitle: LABELS.articles.subtitle, icon: '✎' });
          containers.articles.appendChild(li);
          items.push(li);
          totalCount++;
        });

        if (arts.length > cap) {
          const href = `/search?q=${encodeURIComponent(q)}&type=${TYPE_PARAM.articles}`;
          const more = makeShowMoreItem(LABELS.articles.more, href);
          containers.articles.appendChild(more);
          items.push(more);
          totalCount++;
        }
      }

      if (totalCount > 0) {
        viewAllLink.hidden = false;
        viewAllLink.href = '/search?q=' + encodeURIComponent(q);
        viewAllLink.textContent = `Bekijk alle resultaten voor “${q}”`;
        openPanel();
        setActive(-1);
      } else {
        viewAllLink.hidden = true;
        closePanel();
      }
    }

    const onInput = debounce(async (e) => {
      const q = e.target.value.trim();
      if (!q) {
        clearSections();
        closePanel();
        return;
      }
      try {
        const json = await fetchSuggestions(q);
        buildResults(json, q);
      } catch (err) {
        clearSections();
        closePanel();
      }
    }, 180);

    input.addEventListener('input', onInput);

    input.addEventListener('keydown', (e) => {
      if (panel.hidden) return;
      if (e.key === 'Escape') {
        closePanel();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!items.length) return;
        setActive((activeIndex + 1) % items.length);
        items[activeIndex]?.scrollIntoView({ block: 'nearest' });
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!items.length) return;
        setActive((activeIndex - 1 + items.length) % items.length);
        items[activeIndex]?.scrollIntoView({ block: 'nearest' });
      }
      if (e.key === 'Enter' && activeIndex > -1) {
        e.preventDefault();
        const link = items[activeIndex].querySelector('a');
        if (link) window.location.href = link.href;
      }
    });

    // Klik buiten deze root => sluit alleen dit paneel
    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) closePanel();
    });

    input.addEventListener('focus', () => {
      if (resultsEl.querySelector('[role="option"]')) openPanel();
    });
  });
})();
